import React, { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";

import api from "../api";
import { venueDetailsMapping } from "../misc/venueDetailsMapping";
import DangerIcon from "../assets/modal-icon-danger.png";
import { pinkIcon, blueIcon, greyIcon } from "../components/MapIcons";
import { isEditLocked } from "../utils/isEditLocked";
import { MapResizer } from "../utils/MapResizer";

import "../styles/FindVenues.css";

const DEFAULT_MOCK_STATUS = {
  subscription: { plan: "premium", credits_remaining: 99 },
};

function getMaxAllowed(status) {
  if (isEditLocked(status)) return 0;
  const plan = status?.subscription?.plan;
  return plan === "trial" || plan === "essentials" ? 1 : Infinity;
}

export default function FindVenues({
  mock = false,
  user_id: injectedId,
  mockAccountStatus = DEFAULT_MOCK_STATUS,
  fillHeight = false,
  mapId,
}) {
  const mainFlexStyle = fillHeight
    ? { display: "flex", flexDirection: "column", height: "100%" }
    : undefined;
  const pageFlexStyle = fillHeight
    ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
    : undefined;
  const mapWrapStyle = fillHeight ? { flex: 1, minHeight: 0 } : undefined;

  const { user_id: urlId } = useParams();
  const user_id = injectedId || urlId;
  const navigate = useNavigate();
  const location = useLocation();

  const [accountStatus, setAccountStatus] = useState(null);
  const [trialAllowed, setTrialAllowed] = useState([]);
  const [selected, setSelected] = useState(() =>
    Array.isArray(location.state?.selectedVenues)
      ? location.state.selectedVenues
      : []
  );
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [mapHeight, setMapHeight] = useState("650px");
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  const fetchAccountStatus = useCallback(() => {
    if (mock) {
      setAccountStatus(mockAccountStatus);
      return;
    }
    api
      .get("/account_status/", { params: { user_id } })
      .then(({ data }) => {
        if (data.status === "success") setAccountStatus(data.account_status);
      })
      .catch((err) => console.error("Error fetching account status", err));
  }, [mock, user_id, mockAccountStatus]);

  useEffect(fetchAccountStatus, [fetchAccountStatus]);
  const isTrial = accountStatus?.subscription?.plan === "trial";

  useEffect(() => {
    if (fillHeight) return;
    const resize = () => {
      const newH = window.innerWidth < 768 ? "480px" : "650px";
      setMapHeight(newH);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [fillHeight]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, [mapHeight]);

  useEffect(() => {
    const tryLabelZoom = () => {
      const plus = document.querySelector(".leaflet-control-zoom-in");
      if (plus && !plus.id) plus.id = "fv-zoom-in";
    };
    tryLabelZoom();
    const t = setTimeout(tryLabelZoom, 300);
    return () => clearTimeout(t);
  }, []);

  const allVenues = Object.entries(venueDetailsMapping)
    .filter(([_, v]) => v.latitude && v.longitude)
    .map(([key, v]) => ({
      name: key,
      latitude: v.latitude,
      longitude: v.longitude,
      title: v.title,
      caption: v.caption,
      imageUrl: v.imageUrl,
    }));

  useEffect(() => {
    if (accountStatus?.subscription?.plan !== "trial" || trialAllowed.length)
      return;

    api
      .get("/trial_venues/", { params: { user_id } })
      .then(({ data }) => {
        if (data.status === "success" && Array.isArray(data.venues)) {
          setTrialAllowed(data.venues);
        }
      })
      .catch((err) => console.error("trial_venues API failed", err));
  }, [accountStatus, trialAllowed.length, user_id]);

  const handleMarkerClick = (venueName) => {
    if (
      accountStatus?.subscription?.plan === "trial" &&
      !trialAllowed.includes(venueName)
    ) {
      setModalMessage(
        "This venue isn’t available on the trial plan. Upgrade to unlock every location."
      );
      setShowModal(true);
      return;
    }
    if (!accountStatus) {
      if (!mock) alert("Loading account status…");
      return;
    }
    const max = getMaxAllowed(accountStatus);
    if (selected.includes(venueName)) {
      setSelected((prev) => prev.filter((n) => n !== venueName));
      return;
    }
    if (selected.length >= max) {
      setModalMessage(
        "Due to your current plan, you can only select one venue. Upgrade to pick more."
      );
      setShowModal(true);
      return;
    }
    setSelected((prev) => [...prev, venueName]);
  };

  const gotoFilter = () =>
    navigate(`/parameters/${user_id}`, { state: { selectedVenues: selected } });
  const gotoSkip = () =>
    navigate(`/preferences/${user_id}`, {
      state: { selectedVenues: selected },
    });

  const maxAllowed = getMaxAllowed(accountStatus);
  const instructionText = isEditLocked(accountStatus)
    ? "Your plan is view-only. You can review your venue below, but need to renew or upgrade to add more."
    : `Welcome! Select ${
        maxAllowed === 1 ? "1 venue" : "multiple venues"
      } by clicking pins on the map.`;

  const secondaryDisabled = selected.length === 0;
  const primaryLabel =
    selected.length === 0
      ? "Use filters to find venues"
      : "Next: Filter Venues";

  return (
    <div className="find-venues-main-container" style={mainFlexStyle}>
      <header className="logo">
        <Link to={`/dashboard/${user_id}`}>BestDay2Marry</Link>
      </header>

      <div className="find-venues-page-container" style={pageFlexStyle}>
        <div className="page-instructions">
          <p>{instructionText}</p>
        </div>

        <div
          className="map-container"
          id="fv-map-wrap"
          ref={containerRef}
          style={mapWrapStyle}
        >
          <MapContainer
            id={mapId}
            center={[37.5, -119.5]}
            zoom={6}
            style={{ height: fillHeight ? "100%" : mapHeight, width: "100%" }}
            whenCreated={(m) => {
              mapRef.current = m;

              const node = containerRef.current;
              if (node) {
                const ro = new ResizeObserver(() => {
                  m.invalidateSize();
                });
                ro.observe(node);
                m.once("unload", () => ro.disconnect());
              }

              // fix initial grey pane: re-check size after first paint
              requestAnimationFrame(() => {
                m.invalidateSize();
              });
            }}
          >
            <MapResizer />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {allVenues.map((v) => {
              const plan = accountStatus?.subscription?.plan;
              const unlocked =
                plan !== "trial" || trialAllowed.includes(v.name);
              const isSelected = selected.includes(v.name);

              const icon = unlocked
                ? isSelected
                  ? pinkIcon
                  : blueIcon
                : greyIcon;

              return (
                <Marker
                  key={v.name}
                  position={[v.latitude, v.longitude]}
                  icon={icon}
                  eventHandlers={{ click: () => handleMarkerClick(v.name) }}
                >
                  <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                    <div className="tooltip-content">
                      {v.imageUrl && (
                        <img
                          src={v.imageUrl}
                          alt={v.title}
                          className="tooltip-image"
                        />
                      )}
                      <strong>{v.title}</strong>
                      <p>{v.caption}</p>
                      {!unlocked && (
                        <em style={{ color: "#888" }}>
                          Upgrade your plan to view this venue
                        </em>
                      )}
                    </div>
                  </Tooltip>
                </Marker>
              );
            })}
          </MapContainer>

          <div className="map-legend">
            <div className="legend-item">
              <img
                src={pinkIcon.options.iconUrl}
                alt="Selected"
                className="legend-icon"
              />{" "}
              Selected
            </div>
            <div className="legend-item">
              <img
                src={blueIcon.options.iconUrl}
                alt="Available"
                className="legend-icon"
              />{" "}
              Available
            </div>
            <div className="legend-item">
              <img
                src={greyIcon.options.iconUrl}
                alt="Locked"
                className="legend-icon"
              />{" "}
              Locked
            </div>
          </div>
        </div>

        <div className="selected-venues-bucket">
          <h3>Selected Venues</h3>
          {selected.length === 0 ? (
            <p>No venues selected.</p>
          ) : (
            <div className="selected-venues-list">
              {selected.map((name) => {
                const d = venueDetailsMapping[name];
                return d ? (
                  <div key={name} className="selected-venue-item">
                    <img
                      src={d.imageUrl}
                      alt={d.title}
                      className="selected-venue-image"
                    />
                    <p>{d.title}</p>
                  </div>
                ) : null;
              })}
            </div>
          )}
        </div>

        <div className="sticky-footer">
          <button
            type="button"
            className={`btn btn-primary${isTrial ? " btn-disabled" : ""}`}
            onClick={isTrial ? undefined : gotoFilter}
            disabled={isTrial}
            title={
              isTrial
                ? "Upgrade your plan to access filters"
                : "Filter venues by your preferences"
            }
            aria-label={primaryLabel}
          >
            {primaryLabel}
          </button>

          <button
            type="button"
            className={`btn btn-primary${
              secondaryDisabled ? " btn-disabled" : ""
            }`}
            onClick={secondaryDisabled ? undefined : gotoSkip}
            disabled={secondaryDisabled}
            title={
              secondaryDisabled
                ? "Select at least one venue to proceed"
                : "Skip ahead to date matching"
            }
            aria-label="Go to date matching"
          >
            Go to date matching →
          </button>
        </div>

        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <img src={DangerIcon} alt="warning" className="danger-icon" />
              <p>{modalMessage}</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
